/**
 * Store Locator Web Component
 */

if (!customElements.get('store-locator')) {
  customElements.define(
    'store-locator',
    class StoreLocator extends HTMLElement {
      constructor() {
        super();

        this.map = null;
        this.markers = [];
        this.markerClusterGroup = null;
        this.stores = [];
        this.filteredStores = [];
        this.selectedStoreId = null;
        this.activeCategory = 'instore';

        this.handleSearch = this.debounce(this.handleSearch.bind(this), 500);
        this.handleTabClick = this.handleTabClick.bind(this);
        this.handleCardClick = this.handleCardClick.bind(this);
        this.handleCardKeydown = this.handleCardKeydown.bind(this);
        this.closeMobilePopup = this.closeMobilePopup.bind(this);
      }

      connectedCallback() {
        this.mapContainer = this.querySelector('[data-map-container]');
        this.storeList = this.querySelector('[data-store-list]');
        this.searchInput = this.querySelector('[data-search-input]');
        this.searchButton = this.querySelector('[data-search-button]');
        this.categoryTabs = this.querySelectorAll('[data-category-tab]');
        this.storeCards = this.querySelectorAll('[data-store-card]');
        this.mobilePopup = this.querySelector('[data-mobile-popup]');
        this.mobilePopupContent = this.querySelector('[data-popup-content]');
        this.popupCloseBtn = this.querySelector('[data-popup-close]');

        this.defaultLat = parseFloat(this.dataset.defaultLat) || 48.8566;
        this.defaultLng = parseFloat(this.dataset.defaultLng) || 2.3522;
        this.defaultZoom = parseInt(this.dataset.defaultZoom) || 5;
        this.activeCategory = this.dataset.defaultTab || 'instore';

        this.loadStoreData();
        this.initMap();
        this.initEventListeners();
        this.filterStores();
      }

      disconnectedCallback() {
        if (this.map) {
          this.map.remove();
          this.map = null;
        }

        if (this.searchInput) {
          this.searchInput.removeEventListener('input', this.handleSearch);
        }
        if (this.searchButton) {
          this.searchButton.removeEventListener('click', this.handleSearch);
        }
        this.categoryTabs.forEach((tab) => {
          tab.removeEventListener('click', this.handleTabClick);
        });
        this.storeCards.forEach((card) => {
          card.removeEventListener('click', this.handleCardClick);
          card.removeEventListener('keydown', this.handleCardKeydown);
        });
        if (this.popupCloseBtn) {
          this.popupCloseBtn.removeEventListener('click', this.closeMobilePopup);
        }
      }

      loadStoreData() {
        const jsonScript = this.querySelector('[data-stores-json]');
        if (jsonScript) {
          try {
            this.stores = JSON.parse(jsonScript.textContent);
          } catch (e) {
            console.error('Failed to parse store data:', e);
            this.stores = [];
          }
        }
      }

      initMap() {
        if (!this.mapContainer || typeof L === 'undefined') {
          console.warn('Map container or Leaflet not available');
          return;
        }

        this.map = L.map(this.mapContainer, {
          center: [this.defaultLat, this.defaultLng],
          zoom: this.defaultZoom,
          zoomControl: true,
          scrollWheelZoom: true,
        });

        L.tileLayer('https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png', {
          attribution: '&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a>',
          maxZoom: 19,
        }).addTo(this.map);

        this.markerClusterGroup = L.markerClusterGroup({
          showCoverageOnHover: false,
          maxClusterRadius: 50,
          spiderfyOnMaxZoom: true,
          disableClusteringAtZoom: 15,
          iconCreateFunction: (cluster) => this.createClusterIcon(cluster),
        });

        this.map.addLayer(this.markerClusterGroup);
      }

      createClusterIcon(cluster) {
        const count = cluster.getChildCount();
        return L.divIcon({
          html: `<div class="store-locator__cluster-icon">${count}</div>`,
          className: 'store-locator__cluster',
          iconSize: [24, 24],
        });
      }

      createMarkerIcon(isSelected = false) {
        const markerSvg = `
          <svg width="32" height="47" viewBox="0 0 32 47" fill="none" xmlns="http://www.w3.org/2000/svg">
            <rect width="32" height="32" fill="#1B1C1D"/>
            <g transform="translate(4, 4)">
              <path d="M24 10.2861L18 4.28613L12 10.2861L6 4.28613L0 10.2861V0H24V10.2861Z" fill="white"/>
              <path d="M24 16.7754V23.6328L18 17.6328L12 23.6328L6 17.6328L0 23.6328V16.7754L6 10.7754L12 16.7754L18 10.7754L24 16.7754Z" fill="white"/>
            </g>
            <path d="M0 32L16 47L32 32Z" fill="#1B1C1D"/>
          </svg>
        `;
        return L.divIcon({
          html: markerSvg,
          className: 'store-locator__marker',
          iconSize: [32, 47],
          iconAnchor: [16, 47],
          popupAnchor: [0, -50],
        });
      }

      initEventListeners() {
        if (this.searchInput) {
          this.searchInput.addEventListener('input', this.handleSearch);
          this.searchInput.addEventListener('keydown', (e) => {
            if (e.key === 'Enter') {
              e.preventDefault();
              this.handleSearch();
            }
          });
        }

        if (this.searchButton) {
          this.searchButton.addEventListener('click', this.handleSearch);
        }

        this.categoryTabs.forEach((tab) => {
          tab.addEventListener('click', this.handleTabClick);
        });

        this.storeCards.forEach((card) => {
          card.addEventListener('click', this.handleCardClick);
          card.addEventListener('keydown', this.handleCardKeydown);
        });

        if (this.popupCloseBtn) {
          this.popupCloseBtn.addEventListener('click', this.closeMobilePopup);
        }
      }

      handleSearch() {
        this.filterStores();
      }

      handleTabClick(e) {
        const tab = e.currentTarget;
        const category = tab.dataset.category;

        if (category === this.activeCategory) return;

        this.activeCategory = category;

        this.categoryTabs.forEach((t) => {
          const isActive = t.dataset.category === category;
          t.classList.toggle('store-locator__tab--active', isActive);
          t.setAttribute('aria-selected', isActive);
        });

        this.selectedStoreId = null;
        this.closeMobilePopup();
        this.filterStores();
      }

      handleCardClick(e) {
        const card = e.currentTarget;
        const storeId = card.dataset.storeId;
        this.selectStore(storeId);
      }

      handleCardKeydown(e) {
        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault();
          this.handleCardClick(e);
        }
      }

      filterStores() {
        const searchTerm = this.searchInput?.value.toLowerCase().trim() || '';

        this.filteredStores = this.stores.filter((store) => {
          const matchesCategory = store.category === this.activeCategory;
          const matchesSearch =
            !searchTerm ||
            (store.name && store.name.toLowerCase().includes(searchTerm)) ||
            (store.country && store.country.toLowerCase().includes(searchTerm)) ||
            (store.city && store.city.toLowerCase().includes(searchTerm)) ||
            (store.address && store.address.toLowerCase().includes(searchTerm));

          return matchesCategory && matchesSearch;
        });

        this.updateStoreList();
        this.updateMapMarkers();
        this.updateCounts();
      }

      updateStoreList() {
        const filteredIds = this.filteredStores.map((s) => s.id);

        this.storeCards.forEach((card) => {
          const storeId = card.dataset.storeId;
          const category = card.dataset.category;
          const isVisible = category === this.activeCategory && filteredIds.includes(storeId);
          card.classList.toggle('store-locator__card--hidden', !isVisible);
          card.classList.toggle('store-locator__card--selected', storeId === this.selectedStoreId);
        });
      }

      updateCounts() {
        const searchTerm = this.searchInput?.value.toLowerCase().trim() || '';

        if (!searchTerm) {
          const onlineCount = this.stores.filter((s) => s.category === 'online').length;
          const instoreCount = this.stores.filter((s) => s.category === 'instore').length;

          const onlineCountEl = this.querySelector('[data-count="online"]');
          const instoreCountEl = this.querySelector('[data-count="instore"]');

          if (onlineCountEl) onlineCountEl.textContent = onlineCount;
          if (instoreCountEl) instoreCountEl.textContent = instoreCount;
        } else {
          const onlineFiltered = this.stores.filter(
            (s) =>
              s.category === 'online' &&
              ((s.name && s.name.toLowerCase().includes(searchTerm)) ||
                (s.country && s.country.toLowerCase().includes(searchTerm)) ||
                (s.city && s.city.toLowerCase().includes(searchTerm)) ||
                (s.address && s.address.toLowerCase().includes(searchTerm)))
          ).length;

          const instoreFiltered = this.stores.filter(
            (s) =>
              s.category === 'instore' &&
              ((s.name && s.name.toLowerCase().includes(searchTerm)) ||
                (s.country && s.country.toLowerCase().includes(searchTerm)) ||
                (s.city && s.city.toLowerCase().includes(searchTerm)) ||
                (s.address && s.address.toLowerCase().includes(searchTerm)))
          ).length;

          const onlineCountEl = this.querySelector('[data-count="online"]');
          const instoreCountEl = this.querySelector('[data-count="instore"]');

          if (onlineCountEl) onlineCountEl.textContent = onlineFiltered;
          if (instoreCountEl) instoreCountEl.textContent = instoreFiltered;
        }
      }

      updateMapMarkers() {
        if (!this.map || !this.markerClusterGroup) return;

        this.markerClusterGroup.clearLayers();
        this.markers = [];

        if (this.activeCategory !== 'instore') return;

        this.filteredStores.forEach((store) => {
          if (store.latitude && store.longitude) {
            const marker = this.createMarker(store);
            this.markers.push(marker);
            this.markerClusterGroup.addLayer(marker);
          }
        });

        if (this.markers.length > 0) {
          const group = L.featureGroup(this.markers);
          this.map.fitBounds(group.getBounds().pad(0.1), {
            maxZoom: 12,
          });
        }
      }

      createMarker(store) {
        const marker = L.marker([store.latitude, store.longitude], {
          icon: this.createMarkerIcon(),
        });

        marker.storeId = store.id;

        const popupContent = this.createPopupContent(store);
        marker.bindPopup(popupContent, {
          className: 'store-locator__popup',
          maxWidth: 460,
          minWidth: 300,
          offset: [0, 0],
        });

        marker.on('click', () => {
          this.selectStore(store.id, false);
        });

        return marker;
      }

      createPopupContent(store) {
        const logoHtml = store.logo
          ? `<img src="${store.logo}" alt="${store.name}" class="store-locator__popup-logo">`
          : '';

        const websiteHtml = store.website
          ? `<a href="${store.website}" target="_blank" rel="noopener" class="store-locator__popup-link">${this.extractDomain(store.website)}</a>`
          : '';

        return `
          <div class="store-locator__popup-inner">
            ${logoHtml}
            <div class="store-locator__popup-info">
              <div class="store-locator__popup-details">
                <h4 class="store-locator__popup-name">${store.name || ''}</h4>
                <p class="store-locator__popup-address">${store.address || ''}</p>
              </div>
              ${websiteHtml}
            </div>
          </div>
        `;
      }

      extractDomain(url) {
        try {
          const domain = new URL(url).hostname;
          return domain.replace('www.', '');
        } catch {
          return url;
        }
      }

      selectStore(storeId, centerMap = true) {
        this.selectedStoreId = storeId;

        this.storeCards.forEach((card) => {
          const isSelected = card.dataset.storeId === storeId;
          card.classList.toggle('store-locator__card--selected', isSelected);

          if (isSelected) {
            card.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
          }
        });

        const store = this.stores.find((s) => s.id === storeId);
        if (!store) return;

        if (this.activeCategory === 'instore' && store.latitude && store.longitude) {
          if (centerMap) {
            this.map.setView([store.latitude, store.longitude], 15);
          }

          const marker = this.markers.find((m) => m.storeId === storeId);
          if (marker) {
            marker.openPopup();
          }

          this.showMobilePopup(store);
        }
      }

      showMobilePopup(store) {
        if (!this.mobilePopup || !this.mobilePopupContent) return;

        this.mobilePopupContent.innerHTML = this.createPopupContent(store);
        this.mobilePopup.setAttribute('aria-hidden', 'false');
      }

      closeMobilePopup() {
        if (!this.mobilePopup) return;

        this.mobilePopup.setAttribute('aria-hidden', 'true');

        this.selectedStoreId = null;
        this.storeCards.forEach((card) => {
          card.classList.remove('store-locator__card--selected');
        });

        if (this.map) {
          this.map.closePopup();
        }
      }

      debounce(func, wait) {
        let timeout;
        return function executedFunction(...args) {
          const later = () => {
            clearTimeout(timeout);
            func(...args);
          };
          clearTimeout(timeout);
          timeout = setTimeout(later, wait);
        };
      }
    }
  );
}
